import { QuizConfig } from "../../components/education/QuizEngine";

// Sample quiz content for color theory lessons
export const quizContentLibrary: Record<string, QuizConfig> = {
  "color-basics-quiz": {
    id: "color-basics-quiz",
    title: "Color Basics Quiz",
    description: "Test your understanding of fundamental color theory concepts",
    questions: [
      {
        id: "cb-q1",
        type: "multiple-choice",
        question:
          "What are the three primary colors in traditional color theory (RYB model)?",
        options: [
          "Red, Yellow, Blue",
          "Red, Green, Blue",
          "Cyan, Magenta, Yellow",
          "Red, Orange, Yellow",
        ],
        correctAnswer: "Red, Yellow, Blue",
        explanation:
          "In traditional color theory (RYB - Red, Yellow, Blue), these three colors cannot be created by mixing other colors and are used to create all other colors.",
        points: 10,
        difficulty: "beginner",
        category: "color-theory",
      },
      {
        id: "cb-q2",
        type: "true-false",
        question: "Secondary colors are created by mixing two primary colors.",
        correctAnswer: "True",
        explanation:
          "Secondary colors (orange, green, purple) are created by mixing equal parts of two primary colors.",
        points: 10,
        difficulty: "beginner",
        category: "color-theory",
      },
      {
        id: "cb-q3",
        type: "multiple-selection",
        question:
          "Which of the following are secondary colors? Select all that apply.",
        options: ["Orange", "Green", "Purple", "Red", "Yellow"],
        correctAnswer: ["Orange", "Green", "Purple"],
        explanation:
          "The three secondary colors are orange (red + yellow), green (blue + yellow), and purple (red + blue).",
        points: 15,
        difficulty: "beginner",
        category: "color-theory",
      },
      {
        id: "cb-q4",
        type: "scenario",
        scenario:
          "A web designer is creating a color palette for a children's educational website. They want to use bright, engaging colors that work well together.",
        question:
          "Which color harmony would be most appropriate for creating a playful, energetic feel?",
        options: [
          "Complementary colors",
          "Analogous colors",
          "Triadic colors",
          "Monochromatic colors",
        ],
        correctAnswer: "Triadic colors",
        explanation:
          "Triadic color schemes use three evenly spaced colors on the color wheel, creating vibrant, energetic palettes perfect for children's content.",
        points: 15,
        difficulty: "intermediate",
        category: "harmony",
      },
      {
        id: "cb-q5",
        type: "fill-blank",
        question:
          "Colors that are located directly opposite each other on the color wheel are called _______ colors.",
        correctAnswer: "complementary",
        explanation:
          "Complementary colors are positioned opposite each other on the color wheel and create the highest contrast when used together.",
        points: 10,
        difficulty: "beginner",
        category: "harmony",
      },
    ],
    timeLimit: 15,
    passingScore: 80,
    allowRetries: true,
    showCorrectAnswers: true,
    randomizeQuestions: false,
    randomizeOptions: true,
    difficulty: "beginner",
    category: "color-basics",
  },

  "color-wheel-quiz": {
    id: "color-wheel-quiz",
    title: "Color Wheel Fundamentals Quiz",
    description:
      "Test your knowledge of color wheel structure and relationships",
    questions: [
      {
        id: "cw-q1",
        type: "multiple-choice",
        question:
          "How many degrees separate complementary colors on the color wheel?",
        options: ["90°", "120°", "180°", "270°"],
        correctAnswer: "180°",
        explanation:
          "Complementary colors are positioned directly opposite each other on the color wheel, which is 180 degrees apart in a 360-degree circle.",
        points: 10,
        difficulty: "beginner",
        category: "color-wheel",
      },
      {
        id: "cw-q2",
        type: "matching",
        question: "Match each color harmony with its description:",
        matches: [
          { left: "Analogous", right: "Colors adjacent to each other" },
          { left: "Triadic", right: "Three evenly spaced colors" },
          {
            left: "Split-Complementary",
            right: "Base color plus two adjacent to complement",
          },
          { left: "Tetradic", right: "Four colors in two complementary pairs" },
        ],
        correctAnswer: {
          "0": "Colors adjacent to each other",
          "1": "Three evenly spaced colors",
          "2": "Base color plus two adjacent to complement",
          "3": "Four colors in two complementary pairs",
        },
        explanation:
          "Understanding these color harmony relationships helps create effective color schemes.",
        points: 20,
        difficulty: "intermediate",
        category: "harmony",
      },
      {
        id: "cw-q3",
        type: "multiple-selection",
        question:
          "Which of the following are examples of warm colors? Select all that apply.",
        options: ["Red", "Orange", "Yellow", "Blue", "Green"],
        correctAnswer: ["Red", "Orange", "Yellow"],
        explanation:
          "Warm colors are typically associated with heat, energy, and brightness. Red, orange, and yellow are the primary warm colors.",
        points: 15,
        difficulty: "beginner",
        category: "color-theory",
      },
      {
        id: "cw-q4",
        type: "scenario",
        scenario:
          "A graphic designer is creating a logo for a luxury spa that needs to convey calmness and relaxation.",
        question:
          "Which color temperature would be most appropriate for this brand?",
        options: [
          "Warm colors (reds, oranges)",
          "Cool colors (blues, greens)",
          "Neutral colors (grays, beiges)",
          "High contrast complementary colors",
        ],
        correctAnswer: "Cool colors (blues, greens)",
        explanation:
          "Cool colors are associated with calmness, tranquility, and relaxation, making them ideal for spa and wellness brands.",
        points: 15,
        difficulty: "intermediate",
        category: "psychology",
      },
      {
        id: "cw-q5",
        type: "true-false",
        question:
          "Tertiary colors are created by mixing a primary color with an adjacent secondary color.",
        correctAnswer: "True",
        explanation:
          "Tertiary colors (like red-orange, yellow-green, blue-purple) are created by mixing equal parts of a primary and an adjacent secondary color.",
        points: 10,
        difficulty: "beginner",
        category: "color-theory",
      },
    ],
    timeLimit: 20,
    passingScore: 80,
    allowRetries: true,
    showCorrectAnswers: true,
    randomizeQuestions: false,
    randomizeOptions: true,
    difficulty: "beginner",
    category: "color-wheel",
  },

  "harmony-quiz": {
    id: "harmony-quiz",
    title: "Color Harmony Quiz",
    description: "Test your understanding of color harmonies and relationships",
    questions: [
      {
        id: "h-q1",
        type: "multiple-choice",
        question: "Which color harmony creates the highest contrast?",
        options: ["Analogous", "Complementary", "Triadic", "Monochromatic"],
        correctAnswer: "Complementary",
        explanation:
          "Complementary colors create the highest contrast because they are positioned opposite each other on the color wheel.",
        points: 10,
        difficulty: "beginner",
        category: "harmony",
      },
      {
        id: "h-q2",
        type: "fill-blank",
        question: "A _______ color scheme uses variations of a single color.",
        correctAnswer: "monochromatic",
        explanation:
          "Monochromatic color schemes use different tints, tones, and shades of a single hue, creating subtle and sophisticated palettes.",
        points: 10,
        difficulty: "beginner",
        category: "harmony",
      },
      {
        id: "h-q3",
        type: "scenario",
        scenario:
          "An interior designer is creating a color scheme for a modern office space that needs to feel balanced and professional.",
        question:
          "Which color harmony would be most suitable for this environment?",
        options: [
          "High-contrast complementary",
          "Calm analogous",
          "Energetic triadic",
          "Bold split-complementary",
        ],
        correctAnswer: "Calm analogous",
        explanation:
          "Analogous color schemes create harmony and are less distracting, making them ideal for professional environments where focus is important.",
        points: 15,
        difficulty: "intermediate",
        category: "application",
      },
      {
        id: "h-q4",
        type: "multiple-selection",
        question:
          "Which of the following are characteristics of split-complementary color schemes? Select all that apply.",
        options: [
          "High contrast but less jarring than complementary",
          "Uses three colors",
          "Creates visual tension",
          "Easy to balance",
          "Uses colors adjacent to the complement",
        ],
        correctAnswer: [
          "High contrast but less jarring than complementary",
          "Uses three colors",
          "Easy to balance",
          "Uses colors adjacent to the complement",
        ],
        explanation:
          "Split-complementary schemes offer strong contrast while being more harmonious than pure complementary schemes.",
        points: 20,
        difficulty: "intermediate",
        category: "harmony",
      },
      {
        id: "h-q5",
        type: "true-false",
        question:
          "Tetradic color schemes always use four colors that are evenly spaced on the color wheel.",
        correctAnswer: "False",
        explanation:
          "While some tetradic schemes use evenly spaced colors, most tetradic schemes consist of two complementary pairs, which may not be evenly spaced.",
        points: 15,
        difficulty: "advanced",
        category: "harmony",
      },
    ],
    timeLimit: 25,
    passingScore: 80,
    allowRetries: true,
    showCorrectAnswers: true,
    randomizeQuestions: false,
    randomizeOptions: true,
    difficulty: "intermediate",
    category: "harmony",
  },
};

// Helper function to get quiz config by ID
export const getQuizConfig = (quizId: string): QuizConfig | null => {
  return quizContentLibrary[quizId] || null;
};

// Helper function to get all quizzes for a category
export const getQuizzesByCategory = (category: string): QuizConfig[] => {
  return Object.values(quizContentLibrary).filter(
    (quiz) => quiz.category === category,
  );
};

// Helper function to get quizzes by difficulty
export const getQuizzesByDifficulty = (
  difficulty: "beginner" | "intermediate" | "advanced",
): QuizConfig[] => {
  return Object.values(quizContentLibrary).filter(
    (quiz) => quiz.difficulty === difficulty,
  );
};
